import { Injectable } from '@angular/core';

import { COUNTRY } from '../../models/country.model';

import { EnvironmentService } from '../environment.service';
import { LoggerService } from '../logger.service';
import { SettingsService } from '../settings.service';
import { PlatformService } from '../ssr/platform.service';
import { WindowService } from '../ssr/window.service';

declare global {
  interface Window {
    ApplePay: ApplePay;
  }
}

type CanMakeApplePayPaymentResult =
  | 'This device can make payments.'
  | 'This device cannot make payments.'
  | 'This device can make payments but has no supported card.';

type ApplePayNetwork = 'visa' | 'masterCard' | 'discover';
type ApplePayMerchantCapability = '3ds' | 'debit' | 'credit';
type ApplePayCurrencyCode = 'KWD' | 'AED';
type ApplePayCountryCode = 'KW' | 'AE';
type ApplePayTransactionStatus =
  | 'success'
  | 'failure'
  | 'invalid-billing-address'
  | 'invalid-shipping-address'
  | 'invalid-shipping-contact'
  | 'require-pin'
  | 'incorrect-pin'
  | 'locked-pin';

interface ApplePayOrderDetails {
  items: Array<{ label: string; amount: number }>;
}

interface ApplePayPaymentRequest {
  items: Array<{ label: string; amount: number }>;
  shippingMethods?: Array<{ identifier: string; label: string; detail: string; amount: number }>;
  supportedNetworks: Array<ApplePayNetwork>;
  merchantCapabilities: Array<ApplePayMerchantCapability>;
  merchantIdentifier: string;
  currencyCode: ApplePayCurrencyCode;
  countryCode: ApplePayCountryCode;
  billingAddressRequirement: 'none';
  shippingAddressRequirement: 'none';
  shippingType: 'shipping';
}

interface ApplePayPaymentResponse {
  paymentData: string;
  transactionIdentifier?: string;
  paymentMethodDisplayName?: string;
  paymentMethodNetwork?: string;
  paymentMethodTypeCard?: string;
}

export declare class ApplePay {
  public canMakePayments(): Promise<CanMakeApplePayPaymentResult>;
  public makePaymentRequest(order: ApplePayOrderDetails): Promise<ApplePayPaymentResponse>;
  public completeLastTransaction(status: ApplePayTransactionStatus): void;
}

@Injectable({ providedIn: 'root' })
export class ApplePayPlugin {
  private applePay: ApplePay;

  constructor(
    private windowService: WindowService,
    private loggerService: LoggerService,
    private settingsService: SettingsService,
    private platformService: PlatformService,
    private environmentService: EnvironmentService,
  ) {
    this.applePay =
      this.platformService.areCordovaPluginsAvailable && this.windowService.isWindowDefined
        ? this.windowService.window.ApplePay
        : undefined;
  }

  public getApplePayToken(orderDetails: ApplePayOrderDetails): Promise<string> {
    const emptyTokenResponse = '';

    if (!orderDetails.items?.length) {
      return Promise.resolve(emptyTokenResponse);
    }

    return this.canMakePayments().then((canMakePayments) => {
      if (!canMakePayments) {
        return emptyTokenResponse;
      }

      const currentCountry = this.settingsService.getCountry();

      return this.makePaymentRequest({
        items: orderDetails.items,
        merchantIdentifier: this.environmentService.applePayMerchantId,
        merchantCapabilities: ['3ds', 'debit', 'credit'],
        supportedNetworks: ['visa', 'masterCard', 'discover'],
        countryCode: currentCountry.value === COUNTRY.kuwait.id ? 'KW' : 'AE',
        currencyCode: currentCountry.value === COUNTRY.kuwait.id ? 'KWD' : 'AED',
        shippingType: 'shipping',
        shippingAddressRequirement: 'none',
        billingAddressRequirement: 'none',
      }).then((paymentResponse) => paymentResponse?.paymentData || emptyTokenResponse);
    });
  }

  public completeLastTransaction(status: ApplePayTransactionStatus): void {
    if (!this.applePay) {
      return;
    }

    try {
      this.applePay.completeLastTransaction(status);
    } catch (error: unknown) {
      this.loggerService.error({ component: 'ApplePayPlugin', message: "couldn't complete last transaction", fromPlugin: true, error });
    }
  }

  private canMakePayments(): Promise<boolean> {
    if (!this.applePay) {
      return Promise.resolve(false);
    }

    return this.applePay
      .canMakePayments()
      .then(() => true)
      .catch((error: string) => {
        this.loggerService.error({ component: 'ApplePayPlugin', message: "couldn't check if can make payments", fromPlugin: true, error });
        return false;
      });
  }

  private makePaymentRequest(order: ApplePayPaymentRequest): Promise<ApplePayPaymentResponse> {
    if (!this.applePay) {
      return Promise.resolve({ paymentData: undefined });
    }

    return this.applePay.makePaymentRequest(order).catch((error: string) => {
      this.loggerService.error({ component: 'ApplePayPlugin', message: "couldn't make payment request", fromPlugin: true, error });
      return { paymentData: undefined };
    });
  }
}
